import React, { useState, useEffect } from 'react'
import { useReactiveVar } from '@apollo/client'

import { memoryCurrentGame, friendlyDateFormat, memoryReloadGames } from './lib'
import { removeGameFromStorage, returnGamesFromStorage, returnGameFromStorage } from './logic'

import { Table, Button, Popconfirm, Alert, Progress, Row, Col, Typography } from 'antd'

const { Title } = Typography

const DeleteGame = ({ gameId, deleteGame }) => {
  return (
    // 🔴 THE CONFIRM CLICK MUST NOT REACH THE ROW (22 September 2026). **React portals propagate
    // events through the REACT tree, not the DOM tree** — so antd's popconfirm overlay, which is
    // portaled into `document.body`, still bubbles its "Yes" click up to the row's own onClick.
    // That handler loads the game, so CONFIRMING A DELETE ALSO OPENED THE GAME BEING DELETED: it
    // had just been removed from storage, so the board vanished and the page white-screened.
    // George reported it as "if i delete a game, i think its loading the game up". The
    // `stopPropagation` that was on the button only ever covered the button's own click — the
    // overlay was never inside it. Stopping it here does cover the overlay, because this wrapper
    // is the portal content's React ancestor.
    <span onClick={event => event.stopPropagation()}>
      <Popconfirm
        title='Delete game'
        description='Are you sure to delete this game?'
        okText='Yes'
        cancelText='No'
        onConfirm={() => deleteGame(gameId)}
      >
        <Button type='link' style={{ color: '#d98c8c' }}>Delete</Button>
      </Popconfirm>
    </span>
  )
}

const SaveGamesTitle = ({ games }) => {
  return (
    <Row justify='center'>
      <Title level={4} style={{ margin: 0, color: '#e6edf3' }}>SAVED GAMES</Title>
    </Row>
  )
}

const SavedGamesList = () => {
  const currentGameId = useReactiveVar(memoryCurrentGame)
  const reloadGames = useReactiveVar(memoryReloadGames)

  const loadGame = gameId => {
    // 🔴 A DELETED GAME CANNOT BE OPENED (22 September 2026). Belt to the braces above: the
    // confirm click no longer reaches this handler, and this refuses the dead id outright if
    // anything ever does reach it again — loading a game with no record is what emptied the
    // board and crashed the page.
    if (!returnGameFromStorage(gameId)) return

    const category = 'Game'
    const action = 'loadGame'
    const label = gameId

    window.siteTrack?.('load_game', {
      category,
      action,
      label
    })

    return memoryCurrentGame(gameId)
  }

  const [games, setGames] = useState(returnGamesFromStorage())

  const deleteGame = gameId => {
    const category = 'Game'
    const action = 'deleteGame'
    const label = gameId

    window.siteTrack?.('delete_game', {
      category,
      action,
      label
    })

    // Clear the current-game pointer BEFORE removing from storage so any
    // re-render of the board/solution never reads a now-deleted game.
    if (currentGameId === gameId) memoryCurrentGame(false)
    removeGameFromStorage(gameId)
    setGames(returnGamesFromStorage())
  }

  useEffect(() => {
    setGames(returnGamesFromStorage())
  }, [currentGameId])

  useEffect(() => {
    if (reloadGames) {
      setGames(returnGamesFromStorage())
      memoryReloadGames(false)
    }
  }, [reloadGames, setGames])

  if (games.length === 0) return <Alert message='You have no saved games in local storage' banner />

  const columns = [
    {
      dataIndex: 'game',
      title: 'Game',
      align: 'center',
      render: (value, { gameId, percent }) => {
        // 🔴 NO CARET, AND THE ROW IS CENTRED (George, 22 September 2026: "center the values in
        // the table and remove the right caret and dont let the game colum wrap"). The caret
        // marked which game was open; the card above already names it, and it was the one thing
        // pushing the id off centre. The id is nowrap so a long human-id stays on one line.
        return (
          <Row
            align='middle'
            justify='center'
            onClick={() => loadGame(gameId)}
            wrap={false}
            style={{ gap: 14 }}
          >
            <Col>
              <Button type='link' style={{ padding: 0, color: '#bef264', whiteSpace: 'nowrap' }}>
                {gameId}
              </Button>
            </Col>

            <Col>
              <Progress size='small' steps={5} status='active' percent={percent} strokeColor='#a3e635' />
            </Col>

            <Col>
              <DeleteGame gameId={gameId} deleteGame={deleteGame} />
            </Col>
          </Row>
        )
      }
    },
    {
      dataIndex: 'difficulty',
      title: 'Difficulty',
      align: 'center',
      render: value => value || '—'
    },
    {
      dataIndex: 'mistakeCount',
      title: 'Mistakes',
      align: 'center',
      render: value => value || 0
    },
    {
      dataIndex: 'outcome',
      title: 'Result',
      align: 'center',
      // 🔴 WIN · LOSE · BLANK (George, 22 September 2026: "in the saved games we need to record
      // win or lose of blank unfinished"). An unfinished puzzle renders NOTHING — not a dash,
      // not a placeholder — because there is no result yet, and a dash would read as one.
      render: value => {
        if (value === 'win') return <span style={{ color: '#389e0d', fontWeight: 700 }}>WIN</span>
        if (value === 'lose') return <span style={{ color: '#cf1322', fontWeight: 700 }}>LOSE</span>
        return null
      }
    },
    {
      dataIndex: 'last',
      title: 'Last activity',
      align: 'center',
      render: (value, { createdAt, updatedAt }) => friendlyDateFormat(updatedAt || createdAt)
    }
  ]

  return (
    <Table
      title={() => <SaveGamesTitle games={games} />}
      size='small'
      columns={columns}
      dataSource={games}
      pagination={false}
      showHeader
      bordered
    />
  )
}

export default SavedGamesList
